import React, { useCallback } from "react";
import moment from "moment";
import momentTz from "moment-timezone/builds/moment-timezone-with-data";
import Countdown from "react-countdown";
import {
  GoogleReCaptcha,
  GoogleReCaptchaProvider,
} from "react-google-recaptcha-v3";
import { useParams } from "react-router";
import { connect } from "react-redux";
import Card from "@material-ui/core/Card";
import Grid from "@material-ui/core/Grid";
import CardMedia from "@material-ui/core/CardMedia";
import CardHeader from "@material-ui/core/CardHeader";
import { useStyles } from "../../component/TicketCard";
import {
  CardElement,
  Elements,
  injectStripe,
  StripeProvider,
} from "react-stripe-elements";
import {
  InlineError,
  StyledButton,
  StyledInput,
} from "../../component/Typeset";
import { api } from "../../api";
import SnackbarContent from "@material-ui/core/SnackbarContent";
import InfoIcon from "@material-ui/icons/Info";
import { AlertBox } from "../../component/alert-box";

const BuyTicketCheckout = injectStripe(
  ({ transferTicket, toggleLoadingModal, isLoading, expiresAt, ticketTier, priceStr, clientSecret, ticketTransferUri, stripe }) => {
    const [firstName, setFirstName] = React.useState("");
    const [lastName, setLastName] = React.useState("");
    const [errors, setErrors] = React.useState("");

    const handleSubmit = (ev) => {
      ev.preventDefault();
      toggleLoadingModal(true);
      setErrors("");
      stripe.handleCardPayment(clientSecret).then(async (response) => {
        if (response.error) {
          setErrors(response.error.message);
          toggleLoadingModal(false);
        } else if (
          response.paymentIntent &&
          response.paymentIntent.status === "succeeded"
        ) {
          const transferResponse = await transferTicket({
            paymentId: response.paymentIntent.id,
            first_name: firstName,
            last_name: lastName,
            ticketTransferUri: ticketTransferUri,
          });
          if (transferResponse.data.error) {
            toggleLoadingModal(false);
            alert(transferResponse.data.error);
          } else {
            window.location.href = transferResponse.data.url;
          }
        }
      });
    };

    return (
      <form
        onSubmit={handleSubmit}
        style={{
          width: "100%",
          display: "flex",
          flex: 1,
          flexDirection: "column",
        }}
      >
        <div
          style={{
            flex: 1,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            padding: "16px",
            color: "rgba(255, 255, 255, 0.85)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div style={{ marginBottom: "16px" }}>
              {ticketTier}
              <br />
              <div style={{ fontSize: "1.25rem" }}>
                <b>{priceStr}</b>
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              Remaining Time
              <br />
              <Countdown
                date={expiresAt}
                renderer={({ minutes, seconds }) => {
                  return (
                    <div style={{ fontSize: "1.25rem" }}>
                      <b>
                        {minutes.toString().padStart(2, '0')}:{seconds.toString().padStart(2, '0')}
                      </b>
                    </div>
                  );
                }}
              />
            </div>
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: "0.8rem", marginBottom: 6 }}>
              Name on ticket{" "}
              {(!firstName || !lastName) && (
                <span style={{ color: "#f44336" }}>*required</span>
              )}
            </div>
            <div style={{ display: "flex" }}>
              <StyledInput
                type="text"
                value={firstName}
                placeholder={"First name"}
                onChange={(e) => setFirstName(e.target.value)}
                style={{ flex: 1, marginRight: 8 }}
              />
              <StyledInput
                type="text"
                value={lastName}
                placeholder={"Last name"}
                onChange={(e) => setLastName(e.target.value)}
                style={{ flex: 1, marginLeft: 8 }}
              />
            </div>
          </div>
          <div
            style={{
              fontSize: "0.8rem",
              marginBottom: 6,
            }}
          >
            Card details:
          </div>
          <CardElement
            hidePostalCode
            style={{
              base: {
                fontSize: "14px",
                fontFamily: 'Montserrat, Roboto, "Helvetica Neue", Arial',
                fontSmoothing: "antialiased",
              },
              invalid: {
                color: "#9e2146",
              },
            }}
          />
          {errors && (
            <InlineError style={{ marginTop: 10 }}>{errors}</InlineError>
          )}
        </div>
        <div style={{ display: "flex", padding: "0 16px 16px 16px" }}>
          <StyledButton type="submit" disabled={!firstName || !lastName || isLoading}>
            Pay {priceStr}
          </StyledButton>
        </div>
      </form>
    );
  }
);

const BuyTicket = ({
  getTicketTransferData,
  toggleLoadingModal,
  isLoading,
  transferTicket,
  ticketTransferData,
}) => {
  const classes = useStyles();
  const { ticketTransferUri } = useParams();
  const [stripeConfig, setStripeConfig] = React.useState(null);
  const [captchaToken, setCaptchaToken] = React.useState("");
  const [acknowledgeTerms, setAcknowledgeTerms] = React.useState(false);

  React.useEffect(() => {
    getTicketTransferData(ticketTransferUri);
  }, [ticketTransferUri]);

  const prepareCheckout = useCallback(async () => {
    toggleLoadingModal(true);
    try {
      const res = await api.post(
        `triniq_ticket/v1/transfer_ticket/${ticketTransferUri}`,
        {
          captcha: captchaToken,
        }
      );
      setStripeConfig(res.data);
    } catch (e) {
      alert(
        e.response.data.error ||
          "There was an error processing your request. Please contact us at support@triniq.com"
      );
      await getTicketTransferData(ticketTransferUri);
    }
    toggleLoadingModal(false);
  }, [captchaToken]);

  if (!ticketTransferData) {
    return null;
  }

  const {
    ticket_tier: ticketTier,
    event_thumbnail_url: thumbnailUrl,
    event_title: eventTitle,
    event_start_at: startDateTime,
    event_end_at: endDateTime,
    is_transfer_enabled: isTransferEnabled,
    name_change_end: nameChangeEnd,
    price_str: priceStr,
    expires_at: expiresAt,
    currency,
    actual_price,
    actual_booking_fee,
    ticket_transfer_price,
    event_location,
    event_about,
    event_slug,
    status,
  } = ticketTransferData;
  const startStr = moment(startDateTime).format("D MMM YYYY [at] HH:mm");
  const endStr = moment(endDateTime).format("D MMM YYYY [at] HH:mm");
  const tz = moment.tz.guess();

  return (
    <GoogleReCaptchaProvider reCaptchaKey="6LcHlRwjAAAAADjFS4CqfftFOBlVuiHhS73IfqAR">
      <SnackbarContent
        style={{
          backgroundColor: "#42474c",
          marginBottom: 20,
        }}
        message={
          <span
            id="client-snackbar"
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <InfoIcon style={{ fontSize: 20, marginRight: 10 }} />
            This ticket may not be reserved or on hold whilst you purchase. If
            this ticket is sold before you finalise payment, you will be
            notified and payment will not be deducted from your card.
          </span>
        }
      />
      <Grid container spacing={0}>
        <Grid item md={4} sm={6} xs={12}>
          <Card className={classes.card}>
            <div
              style={{
                width: "100%",
                position: "relative",
              }}
            >
              <CardMedia src={thumbnailUrl} component={"img"} />
            </div>
            <CardHeader
              title={
                <div
                  style={{
                    width: "100%",
                    color: "rgba(255, 255, 255, 0.85)",
                  }}
                >
                  {eventTitle}
                  <div className={classes.subtitle}>
                    <span style={{ whiteSpace: "nowrap" }}>
                      <span style={{ width: 50, display: "inline-block" }}>
                        Start:
                      </span>
                      {startStr}
                    </span>
                    <br />
                    <span style={{ whiteSpace: "nowrap" }}>
                      <span style={{ width: 50, display: "inline-block" }}>
                        End:
                      </span>
                      {endStr}
                    </span>
                  </div>
                </div>
              }
              titleTypographyProps={{
                className: classes.title,
              }}
            />
            <div style={{ flex: 1 }} />
          </Card>
        </Grid>
        <Grid item md={8} sm={6} xs={12}>
          <Card
            className={classes.card}
            style={{
              display: "flex",
              flexDirection: "column",
              position: "relative",
            }}
          >
            {stripeConfig ? (
              <StripeProvider apiKey={stripeConfig.stripe_pk}>
                <Elements>
                  <BuyTicketCheckout
                    toggleLoadingModal={toggleLoadingModal}
                    priceStr={priceStr}
                    ticketTier={ticketTier}
                    paymentId={stripeConfig.stripe_pi_id}
                    clientSecret={stripeConfig.stripe_pi_cs}
                    expiresAt={stripeConfig.expires_at}
                    transferTicket={transferTicket}
                    isLoading={isLoading}
                    ticketTransferUri={ticketTransferUri}
                  />
                </Elements>
              </StripeProvider>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  padding: "16px",
                  flex: 1,
                }}
              >
                <div
                  style={{
                    width: "100%",
                    color: "rgba(255, 255, 255, 0.85)",
                    fontSize: "1.25rem",
                    marginBottom: "24px",
                  }}
                >
                  <div style={{ textTransform: "uppercase" }}>{eventTitle}</div>
                  <div style={{ fontSize: "1rem" }}>{event_location}</div>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <div>{ticketTier}</div>
                  <div style={{ fontSize: "1.25rem" }}>
                    <b>{priceStr}</b>
                  </div>
                  {actual_booking_fee > 0 && (
                    <div style={{ fontSize: "0.85rem" }}>
                      <i>
                        ({currency}
                        {ticket_transfer_price || actual_price} + {currency}
                        {actual_booking_fee} Booking fee)
                      </i>
                    </div>
                  )}
                </div>
                {momentTz.tz(nameChangeEnd, tz) < moment() ? null : (
                  <AlertBox>
                    Ticket resales end on{" "}
                    {momentTz
                      .tz(nameChangeEnd, tz)
                      .format("D MMM YYYY [at] HH:mm z")}
                  </AlertBox>
                )}
                <div style={{ flex: 1 }} />
                <GoogleReCaptcha
                  onVerify={setCaptchaToken}
                  refreshReCaptcha={true}
                />
                {momentTz.tz(nameChangeEnd, tz) < moment() ? (
                  <AlertBox>
                    Resales for this event ended on{" "}
                    {momentTz
                      .tz(nameChangeEnd, tz)
                      .format("D MMM YYYY [at] HH:mm z")}
                    , please contact the event organiser for more assistance.
                  </AlertBox>
                ) : (
                  <>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: 6,
                      }}
                    >
                      <input
                        type="checkbox"
                        id="acknowledgement"
                        checked={acknowledgeTerms}
                        onChange={() => setAcknowledgeTerms(!acknowledgeTerms)}
                      />
                      <label
                        htmlFor="acknowledgement"
                        style={{ fontSize: "0.8rem", marginLeft: 10 }}
                      >
                        I agree to the{" "}
                        <a
                          style={{ color: "white" }}
                          href="https://triniq.com/terms"
                          target="_blank"
                        >
                          terms & conditions of sale
                        </a>
                        .
                      </label>
                    </div>
                    <StyledButton
                      disabled={!captchaToken || !acknowledgeTerms || isLoading}
                      onClick={prepareCheckout}
                    >
                      {expiresAt && expiresAt > Date.now() ?
                      <Countdown
                        date={expiresAt}
                        renderer={({ minutes, seconds }) => {
                          return (
                            <div>
                              Please wait{' '}
                              {minutes.toString().padStart(2, '0')}:{seconds.toString().padStart(2, '0')}
                            </div>
                          );
                        }}
                      /> :  'Checkout'}
                    </StyledButton>
                  </>
                )}
              </div>
            )}

            {(status !== "VALID" || !isTransferEnabled) && (
              <div
                style={{
                  backgroundColor: "rgba(0,0,0,0.85)",
                  position: "absolute",
                  left: 0,
                  right: 0,
                  top: 0,
                  bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "24px",
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <div style={{ marginBottom: "20px" }}>
                    {status !== "VALID" ? (
                      <>This ticket has been sold and is no longer available,</>
                    ) : (
                      <>
                        This ticket may have been withdrawn from sale.
                        <br />
                        Please contact the seller to confirm if this ticket is
                        still available or
                      </>
                    )}{" "}
                    click the button below to be redirected to the general sales
                    page.
                  </div>
                  <a href={`/${event_slug}`} style={{ textDecoration: "none" }}>
                    <StyledButton>OPEN GENERAL SALES PAGE</StyledButton>
                  </a>
                </div>
              </div>
            )}
          </Card>
        </Grid>
      </Grid>
      <div style={{ paddingBottom: 100 }}>
        <Card className={classes.card} style={{ marginTop: 24, padding: 16 }}>
          <div
            style={{
              color: "rgba(255, 255, 255, 0.85)",
              fontSize: "1.25rem",
            }}
          >
            EVENT INFO
          </div>
          <hr style={{ width: "100%" }} />
          <div
            dangerouslySetInnerHTML={{ __html: event_about }}
            style={{ lineHeight: "1.5rem" }}
          />
        </Card>
      </div>
    </GoogleReCaptchaProvider>
  );
};

const mapState = (state) => {
  return {
    ticketTransferData: state.ticket.ticketTransferData,
    isLoading: state.ticket.isLoading,
  };
};

const mapDispatch = (dispatch) => ({
  getTicketTransferData: dispatch.ticket.getTicketTransferData,
  toggleLoadingModal: dispatch.ticket.toggleLoadingModal,
  transferTicket: dispatch.ticket.transferTicket,
});

export default connect(mapState, mapDispatch)(BuyTicket);
